/**
 * Общий UX-слой для страниц создания и редактирования турнира:
 * секционная навигация, липкие оффсеты, горячие клавиши, работа с датами и обложками.
 *
 * Компонент, подключающий миксин, должен определить:
 *  - sections()      — computed со списком секций [{key, label, icon, optional}]
 *  - formIssues()    — computed со списком проблем [{section, label, field}]
 *  - requiredCount() — computed с общим числом обязательных проверок
 *  - submitForm()    — метод сохранения (вызывается по Ctrl/Cmd + S)
 */
export const TournamentFormUxMixin = {
    data() {
        return {
            activeSection: 'main',
            showProblems: false,
            isSaving: false,
            suppressGuard: false,
            stickyFrame: null,
            dateMask: 'YYYY-MM-DD HH:mm',
            coverPreview: null,
            coverMeta: null,
            mobilePreview: null,
            mobileMeta: null,
            dragTarget: null,
        };
    },

    computed: {
        sections() {
            return [];
        },
        formIssues() {
            return [];
        },
        requiredCount() {
            return 1;
        },
        issuesBySection() {
            const map = {};
            this.formIssues.forEach((issue) => {
                (map[issue.section] = map[issue.section] || []).push(issue);
            });
            return map;
        },
        progress() {
            const total = Math.max(this.requiredCount, 1);
            const done = Math.max(total - this.formIssues.length, 0);
            return Math.round((done / total) * 100);
        },
        isReady() {
            return this.formIssues.length === 0;
        },
    },

    mounted() {
        this.updateStickyOffsets();
        window.addEventListener('resize', this.updateStickyOffsets);
        document.addEventListener('keydown', this.onFormKeydown);
    },

    beforeDestroy() {
        window.removeEventListener('resize', this.updateStickyOffsets);
        document.removeEventListener('keydown', this.onFormKeydown);
        if (this.stickyFrame) cancelAnimationFrame(this.stickyFrame);
        this.revokePreviews();
    },

    methods: {
        // ------------------------------------------------ навигация по секциям
        goSection(key) {
            this.activeSection = key;
            this.$nextTick(() => {
                const anchor = this.$refs.sectionTop;
                if (!anchor || !anchor.getBoundingClientRect) return;
                const top = anchor.getBoundingClientRect().top + window.pageYOffset - this.stickyOffset() - 12;
                window.scrollTo({top: Math.max(top, 0), behavior: 'smooth'});
            });
        },

        goToIssue(issue) {
            this.goSection(issue.section);
            if (!issue.field) return;
            this.$nextTick(() => {
                setTimeout(() => {
                    const el = this.$el.querySelector(`[data-field="${issue.field}"]`);
                    if (!el) return;
                    const input = el.querySelector('input, select, textarea') || el;
                    if (input && input.focus) input.focus();
                }, 120);
            });
        },

        stickyOffset() {
            const appHeader = document.querySelector('.page-header');
            const headerHeight = appHeader ? Math.round(appHeader.getBoundingClientRect().height) : 0;
            return headerHeight + 64;
        },

        updateStickyOffsets() {
            if (this.stickyFrame) return;
            this.stickyFrame = requestAnimationFrame(() => {
                this.stickyFrame = null;
                if (!this.$el || !this.$el.style) return;
                const appHeader = document.querySelector('.page-header');
                const headerHeight = appHeader ? Math.round(appHeader.getBoundingClientRect().height) : 0;
                this.$el.style.setProperty('--tf-sticky-top', headerHeight + 'px');
            });
        },

        // ------------------------------------------------ горячие клавиши
        onFormKeydown(event) {
            const isSave = (event.ctrlKey || event.metaKey) && (event.key === 's' || event.key === 'S' || event.key === 'ы' || event.key === 'Ы');
            if (isSave) {
                event.preventDefault();
                if (typeof this.submitForm === 'function') this.submitForm();
            }
        },

        // ------------------------------------------------ текст
        stripHtml(value) {
            if (!value) return '';
            return String(value).replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
        },

        hasText(value) {
            return this.stripHtml(value).length > 0;
        },

        // ------------------------------------------------ даты
        parseDate(value) {
            if (!value) return null;
            if (value instanceof Date) return value;
            const normalized = String(value).trim().replace(' ', 'T');
            const date = new Date(normalized);
            return isNaN(date.getTime()) ? null : date;
        },

        formatModelDate(date) {
            if (!date) return '';
            const pad = (n) => String(n).padStart(2, '0');
            const base = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
            return this.dateMask.includes('ss') ? `${base}:${pad(date.getSeconds())}` : base;
        },

        prettyDate(value) {
            const date = this.parseDate(value);
            if (!date) return '';
            const months = ['янв', 'фев', 'мар', 'апр', 'мая', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];
            const pad = (n) => String(n).padStart(2, '0');
            return `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}, ${pad(date.getHours())}:${pad(date.getMinutes())}`;
        },

        weekday(value) {
            const date = this.parseDate(value);
            if (!date) return '';
            return ['воскресенье', 'понедельник', 'вторник', 'среда', 'четверг', 'пятница', 'суббота'][date.getDay()];
        },

        durationHuman(from, to) {
            const a = this.parseDate(from);
            const b = this.parseDate(to);
            if (!a || !b) return '';
            let minutes = Math.round((b - a) / 60000);
            const sign = minutes < 0 ? '−' : '';
            minutes = Math.abs(minutes);
            const days = Math.floor(minutes / 1440);
            const hours = Math.floor((minutes % 1440) / 60);
            const mins = minutes % 60;
            const parts = [];
            if (days) parts.push(`${days} д`);
            if (hours) parts.push(`${hours} ч`);
            if (mins && !days) parts.push(`${mins} мин`);
            return sign + (parts.join(' ') || '0 мин');
        },

        shiftDate(base, {days = 0, hours = 0, minutes = 0, atHour = null} = {}) {
            const source = this.parseDate(base) || new Date();
            const date = new Date(source.getTime());
            date.setDate(date.getDate() + days);
            date.setHours(date.getHours() + hours);
            date.setMinutes(date.getMinutes() + minutes);
            if (atHour !== null) {
                date.setHours(atHour, 0, 0, 0);
            } else {
                date.setSeconds(0, 0);
            }
            return this.formatModelDate(date);
        },

        // ------------------------------------------------ обложки
        pickFile(refName) {
            const input = this.$refs[refName];
            const el = Array.isArray(input) ? input[0] : input;
            if (el && el.click) el.click();
        },

        readableSize(bytes) {
            if (!bytes && bytes !== 0) return '';
            if (bytes < 1024) return `${bytes} Б`;
            if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} КБ`;
            return `${(bytes / (1024 * 1024)).toFixed(1)} МБ`;
        },

        applyCoverFile(file, kind) {
            if (!file || !/^image\//.test(file.type)) {
                this.$toasted.error('Нужен файл изображения', {position: 'top-right', duration: 3000});
                return;
            }
            const preview = URL.createObjectURL(file);
            const meta = {name: file.name, size: this.readableSize(file.size)};
            if (kind === 'mobile') {
                if (this.mobilePreview) URL.revokeObjectURL(this.mobilePreview);
                this.newMobileImage = file;
                this.mobilePreview = preview;
                this.mobileMeta = meta;
            } else {
                if (this.coverPreview) URL.revokeObjectURL(this.coverPreview);
                this.newImage = file;
                this.coverPreview = preview;
                this.coverMeta = meta;
            }
        },

        onCoverChange(event, kind) {
            const file = event.target.files && event.target.files[0];
            if (file) this.applyCoverFile(file, kind);
            event.target.value = '';
        },

        onCoverDrop(event, kind) {
            this.dragTarget = null;
            const file = event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files[0];
            if (file) this.applyCoverFile(file, kind);
        },

        clearCover(kind) {
            if (kind === 'mobile') {
                if (this.mobilePreview) URL.revokeObjectURL(this.mobilePreview);
                this.newMobileImage = null;
                this.mobilePreview = null;
                this.mobileMeta = null;
            } else {
                if (this.coverPreview) URL.revokeObjectURL(this.coverPreview);
                this.newImage = null;
                this.coverPreview = null;
                this.coverMeta = null;
            }
        },

        revokePreviews() {
            if (this.coverPreview) URL.revokeObjectURL(this.coverPreview);
            if (this.mobilePreview) URL.revokeObjectURL(this.mobilePreview);
        },

        // ------------------------------------------------ прочее
        copyToClipboard(text, message = 'Скопировано') {
            if (!text) return;
            const done = () => this.$toasted.success(message, {position: 'top-right', duration: 2000});
            if (navigator.clipboard && navigator.clipboard.writeText) {
                navigator.clipboard.writeText(text).then(done).catch(() => {
                });
                return;
            }
            const area = document.createElement('textarea');
            area.value = text;
            document.body.appendChild(area);
            area.select();
            try {
                document.execCommand('copy');
                done();
            } finally {
                document.body.removeChild(area);
            }
        },

        confirmLeave() {
            return this.$bvModal.msgBoxConfirm('Изменения не сохранены. Покинуть страницу?', {
                title: 'Несохранённые изменения',
                okTitle: 'Выйти без сохранения',
                cancelTitle: 'Остаться',
                okVariant: 'danger',
                centered: true,
                bodyClass: 'py-4',
            });
        },
    },
};
